import { parseDate } from "@/utils/formatting/date";
import {
  EAttachmentFileType,
  EInviteStatus,
  ENotificationStatus,
  ENotificationType,
  getRefId,
  type IAttachment,
  type IInvite,
  type INotification,
  type ISavedDocument,
} from "findef";

const iconLoaderForType = (notification: INotification) => {
  const type = notification.type;
  switch (type) {
    case ENotificationType.IR_INVESTOR_UPDATE_PUBLISHED:
      return async () => import("assets/img/svg/icons/bullhorn.svg?raw");
    case ENotificationType.SHAREHOLDER_INVITE:
    case ENotificationType.ASSET_ADMIN_INVITE:
      return async () => import("assets/img/svg/icons/rocket.svg?raw");
    case ENotificationType.PROVIDER_SESSION_EXPIRED:
    case ENotificationType.PROVIDER_INVESTMENT_DELETED:
      return async () => import("assets/img/svg/icons/chain-link.svg?raw");
    default: {
      const title = (notification.title || "").toLowerCase();
      if (title.includes("currency")) {
        return async () => import("assets/img/svg/icons/coins.svg?raw");
      }
      return async () => import("assets/img/svg/icons/bell.svg?raw");
    }
  }
};

const NotificationAvatar = defineComponent<{
  notification: ISavedDocument<INotification, string>;
  imageUrl: string | null;
}>(
  (props) => {
    const notification = computed(() => unref(props.notification));
    const imageUrl = computed(() => props.imageUrl);
    const iconLoader = computed(() => iconLoaderForType(notification.value));

    return () => {
      if (imageUrl.value) {
        return (
          <img
            class="rounded-full w-11 h-11 object-cover"
            src={imageUrl.value}
            alt="Notification image"
          />
        );
      }
      return (
        <div class="rounded-full w-11 h-11 flex items-center justify-center bg-gray-100 text-gray-500">
          <Icon src={iconLoader.value} size="20px" fill="currentColor" />
        </div>
      );
    };
  },
  { props: ["notification", "imageUrl"] },
);
import * as fns from "date-fns";
import { FindexClient } from "@/integrations/findex";
import { pushToast } from "components/toast/hooks";
import Icon from "components/icon";
import Button from "components/button";
import Badge from "components/badge";
import { refreshInvestments } from '~/composables/useDataRefresh';
import { useNotifications } from "~/composables/useNotifications";
import { useIR } from "~/composables/useIR";
import { clearModals } from "components/modal/store";

const NotificationItemWithInvite = defineComponent<{
  notification: ISavedDocument<INotification, string>
}>((props) => {
  const notificationsHandle = useNotifications();
  const notification = computed(() => unref(props.notification));
  const notificationId = computed(() => getRefId(notification.value));
  const invite = computed(() => (notification.value.invite!) as unknown as any as IInvite);
  const isAcceptInviteLoading = ref<boolean>(false);
  const isDeclineInviteLoading = ref<boolean>(false);
  const inviteStatus = ref<EInviteStatus | undefined>(
    invite.value.status,
  );
  const date = computed(() => {
    return parseDate((notification.value as any).createdAt);
  });

  const formattedDate = computed(() => {
    try {
      return fns.formatDistance(date.value, new Date(), { addSuffix: true });
    } catch (e) {
      console.error(e);
      return "Invalid Date";
    }
  });

  const attachment = computed(() => {
    if (
      notification.value.attachment &&
      typeof notification.value.attachment === "object"
    ) {
      return notification.value
        .attachment as unknown as any as ISavedDocument<IAttachment>;
    }
    return null;
  });
  const notificationImage = computed((): string | null => {
    if (notification.value.image) return notification.value.image;
    const attach = attachment.value;
    if (attach && attach.fileType === EAttachmentFileType.IMAGE)
      return FindexClient.urlForAttachment(attach);
    return null;
  });
  const acceptInvite = async (inviteId: string) => {
    isAcceptInviteLoading.value = true;
    try {
      await FindexClient.resolveInvite({ inviteId });
      inviteStatus.value = EInviteStatus.RESOLVED;
      pushToast({
        title: "Invite accepted",
        variant: "success",
      });
    } catch (e) {
      pushToast({
        title: "Something went wrong",
        subtitle: "Could not accept invite",
        variant: "error",
      });
      isAcceptInviteLoading.value = false;
      return;
    }

    // Clear loading immediately — the accept is done
    isAcceptInviteLoading.value = false;

    // Fire-and-forget: refresh side effects without blocking the UI
    refreshInvestments();
    notificationsHandle.setNotificationStatus(notificationId.value, ENotificationStatus.READ).catch(console.error);
    const { updateCompanies } = useIR();
    updateCompanies().catch(console.error);
  };

  const declineInvite = async (inviteId: string) => {
    isDeclineInviteLoading.value = true;
    try {
      await FindexClient.declineInvite(inviteId);
      inviteStatus.value = EInviteStatus.DECLINED;
      pushToast({
        title: "Invite declined",
        variant: "success",
      });
      await notificationsHandle.setNotificationStatus(notificationId.value, ENotificationStatus.READ)
    } catch (e) {
      pushToast({
        title: "Something went wrong",
        subtitle: "Could not decline invite",
        variant: "error",
      });
    }
    isDeclineInviteLoading.value = false;
  };

  return () => {
    return (
      <div class="flex px-4 py-3 hover:bg-gray-100 dark:hover:bg-gray-700">
        <div class="shrink-0 relative">
          <NotificationAvatar notification={notification.value} imageUrl={notificationImage.value} />
        </div>
        <div class="w-full ps-3 flex flex-col gap-2">
          <div class="text-gray-500 text-sm dark:text-gray-400">
            {notification.value.title && (
              <div class="font-semibold text-gray-900 dark:text-white">
                {notification.value.title}
              </div>
            )}
            {notification.value.body && <p>{notification.value.body}</p>}
          </div>
          {
            inviteStatus.value === EInviteStatus.PENDING && (
              <div class="flex flex-row gap-1 items-center">
                <Button
                  onClick={(e) => {
                    e.stopPropagation();
                    acceptInvite(
                      getRefId(invite.value),
                    );
                  }}
                  isLoading={isAcceptInviteLoading.value}
                  size="s"
                >
                  Accept
                </Button>
                <Button
                  onClick={(e) => {
                    e.stopPropagation();
                    declineInvite(
                      getRefId(invite.value)
                    );
                  }}
                  isLoading={isDeclineInviteLoading.value}
                  variant="transparent"
                  outlined
                  size="s"
                >
                  Decline
                </Button>
              </div>
            )}
          {
            inviteStatus.value === EInviteStatus.RESOLVED && (
              <Badge class="h-8 w-28" variant="blue">
                Accepted
              </Badge>
            )}
          {
            inviteStatus.value === EInviteStatus.DECLINED && (
              <div class="flex flex-row justify-start">
                <Badge class="h-8 w-28">Declined</Badge>
              </div>
            )}
          <div class="text-xs text-primary-600 dark:text-primary-500">
            {formattedDate.value}
          </div>
        </div>
      </div>
    )
  }
}, { props: ['notification'] });


export const NotificationInvestorUpdateBody = defineComponent<{
  notification: ISavedDocument<INotification, string>;
}>((props) => {
  const notification = computed(() => unref(props.notification));
  const payload = computed(() => (notification.value.payload || {}) as { communicationId: string; assetId: string });
  const router = useRouter();
  const notificationId = computed(() => getRefId(notification.value));
  const notificationsHandle = useNotifications();

  const handleClick = async () => {
    const { communicationId, assetId } = payload.value;
    const [investments] = await FindexClient.getInvestmentsByAssetId(assetId);

    const invs = investments || [];
    if (invs.length <= 0) {
      pushToast({
        variant: 'error',
        title: 'There was an error',
        subtitle: 'Could not find the related investment'
      });
      return;
    }
    const investment = invs[0];
    const investmentId = getRefId(investment);
    // Mark as read and close the panel BEFORE navigating, so the
    // destination page mount doesn't have to compete with extra
    // GET /notifications refresh traffic (rate limit risk on mobile).
    notificationsHandle.setNotificationStatus(notificationId.value, ENotificationStatus.READ).catch(console.error);
    clearModals().catch(console.error);
    router.push(`/portfolio/investments/${investmentId}/communication/${communicationId}`);
  }

  return () => {
    return (
      <div class="w-full">
        <Button size="s" onClick={handleClick}>Open</Button>
      </div>
    );
  };
}, { props: ['notification'] });

export const NotificationItem = defineComponent<{
  notification: ISavedDocument<INotification, string>;
}>((props) => {
  const notification = computed(() => unref(props.notification));
  const date = computed(() => {
    return parseDate((notification.value as any).createdAt);
  });

  const formattedDate = computed(() => {
    try {
      return fns.formatDistance(date.value, new Date(), { addSuffix: true });
    } catch (e) {
      console.error(e);
      return "Invalid Date";
    }
  });

  const attachment = computed(() => {
    if (
      notification.value.attachment &&
      typeof notification.value.attachment === "object"
    ) {
      return notification.value
        .attachment as unknown as any as ISavedDocument<IAttachment>;
    }
    return null;
  });

  const notificationImage = computed((): string | null => {
    if (notification.value.image) return notification.value.image;
    const attach = attachment.value;
    if (attach && attach.fileType === EAttachmentFileType.IMAGE)
      return FindexClient.urlForAttachment(attach);
    return null;
  });


  const bodyContent = computed(() => {
    const not = notification.value;
    switch (not.type) {
      case ENotificationType.IR_INVESTOR_UPDATE_PUBLISHED: {
        return <NotificationInvestorUpdateBody notification={notification.value} />
      };
      default: {
        return notification.value.body ? <p>{notification.value.body}</p> : <span />;
      };
    }
  })


  return () => {
    if (notification.value.invite) {
      return <NotificationItemWithInvite notification={notification.value} />
    }
    return (
      <div class="flex px-4 py-3 hover:bg-gray-100 dark:hover:bg-gray-700">
        <div class="shrink-0 relative">
          <NotificationAvatar notification={notification.value} imageUrl={notificationImage.value} />
        </div>
        <div class="w-full ps-3 flex flex-col gap-2">
          <div class="text-gray-500 text-sm dark:text-gray-400">
            {notification.value.title && (
              <div class="font-semibold text-gray-900 dark:text-white">
                {notification.value.title}
              </div>
            )}
            {bodyContent.value}
          </div>
          <div class="text-xs text-primary-600 dark:text-primary-500">
            {formattedDate.value}
          </div>
        </div>
      </div>
    );
  };
}, { props: ['notification'] });
